import {
    Component,
    OnInit,
    TemplateRef,
    inject,
    viewChild,
} from '@angular/core';
import {
    FormArray,
    FormBuilder,
    FormControl,
    FormGroup,
    UntypedFormArray,
    Validators,
    ɵElement,
    FormsModule,
    ReactiveFormsModule,
} from '@angular/forms';
import {
    MatDialog,
    MatDialogTitle,
    MatDialogContent,
    MatDialogActions,
    MatDialogClose,
} from '@angular/material/dialog';

import {
    CvEntitySectionClass,
    CvEntitySectionDTO,
} from '../../shared/external-model/cv/cv-entity-section-dto';
import {
    CvResearcherSectionClass,
    CvResearcherSectionDTO,
} from '../../shared/external-model/cv/cv-researcher-section-dto';
import { RouterService } from '../../core/router.service';
import { CvCreationService } from './cv-creation.service';
import { CvTemplateHeaderDTO } from '../../shared/external-model/cv/cv-template-header-dto';
import {
    BehaviorSubject,
    catchError,
    EMPTY,
    filter,
    finalize,
    take,
} from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { ViewerFile } from '../../shared/interfaces/viewer-file.interface';
import { CvTemplateDTO } from '../../shared/external-model/cv/cv-template-dto';
import { JwtUtilService } from 'src/app/core/jwt-util.service';
import { MultiLanguageService } from 'src/app/parts/multi-language/multi-language.service';
import { MediaService, SvgViewboxDirective } from '@exl-ng/mulo-core';
import { SEOService } from '../../core/seo.service';
import { ConfigurationHandlerService } from '../../core/configuration-handler.service';
import { CustomTranslatePipe } from '../../shared/pipes/custom-translate.pipe';
import { ArcviewContainerComponent } from '../../parts/arcview/arcview-container/arcview-container.component';
import { AnimatedCheckmarkComponent } from '@exl-ng/mulo-common';
import { CdkAriaLive } from '@angular/cdk/a11y';
import { MatInput } from '@angular/material/input';
import { MatFormField, MatLabel, MatError } from '@angular/material/form-field';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { CvFormatComponent } from './cv-format/cv-format.component';
import { CvInfoComponent } from './cv-info/cv-info.component';
import { CvTemplateComponent } from './cv-template/cv-template.component';
import { MatTabNav, MatTabLink, MatTabNavPanel } from '@angular/material/tabs';
import { MatIcon } from '@angular/material/icon';
import {
    MatAnchor,
    MatButton,
    MatIconButton,
    MatMiniFabButton,
} from '@angular/material/button';
import { AsyncPipe, DOCUMENT } from '@angular/common';
import {
    CenteredBlockLayoutComponent,
    SideMainLayoutComponent,
} from '@exl-ng/mulo-views';

export type CvSectionForm<T = CvEntitySectionDTO | CvResearcherSectionDTO> = {
    [K in keyof T]: ɵElement<T[K], null>;
};

// export type CvSectionForm<T = CvEntitySectionDTO | CvResearcherSectionDTO> = {
//     [K in keyof T]: T[K] extends Array<FormControl<string>>
//     ? FormArray<FormControl<string>>
//     : T[K] extends Record<any, any>
//         ? FormGroup<T[K]>
//         : FormControl<T[K]>;
//   };

@Component({
    selector: 'esp-cv-creation',
    templateUrl: './cv-creation.component.html',
    styleUrls: ['./cv-creation.component.scss'],
    imports: [
        CenteredBlockLayoutComponent,
        SideMainLayoutComponent,
        MatAnchor,
        MatIcon,
        MatButton,
        SvgViewboxDirective,
        MatTabNav,
        MatTabLink,
        MatTabNavPanel,
        CvTemplateComponent,
        CvInfoComponent,
        CvFormatComponent,
        MatDialogTitle,
        FormsModule,
        CdkScrollable,
        MatDialogContent,
        MatFormField,
        MatLabel,
        MatInput,
        ReactiveFormsModule,
        MatError,
        MatDialogActions,
        MatDialogClose,
        CdkAriaLive,
        AnimatedCheckmarkComponent,
        MatIconButton,
        MatMiniFabButton,
        ArcviewContainerComponent,
        AsyncPipe,
        TranslateModule,
        CustomTranslatePipe,
    ],
})
export class CvCreationComponent implements OnInit {
    private document = inject<Document>(DOCUMENT);
    _window = this.document.defaultView;
    private fb = inject(FormBuilder);
    dialog = inject(MatDialog);
    private routerService = inject(RouterService);
    private cvCreationService = inject(CvCreationService);
    private jwtUtilService = inject(JwtUtilService);
    private multiLanguageService = inject(MultiLanguageService);
    private _t = inject(TranslateService);
    private configurationHandlerService = inject(ConfigurationHandlerService);
    private seoService = inject(SEOService);
    media = inject(MediaService);

    readonly savingDialogTmplRef =
        viewChild<TemplateRef<any>>('savingDialogTmpl');
    readonly savedDialogTmplRef =
        viewChild<TemplateRef<any>>('savedDialogTmpl');
    readonly successDialogTmplRef =
        viewChild<TemplateRef<any>>('successDialogTmpl');
    readonly errorDialogTmplRef =
        viewChild<TemplateRef<any>>('errorDialogTmpl');
    readonly previewTemplateRef =
        viewChild<TemplateRef<any>>('previewTemplate');
    steps: UntypedFormArray;
    infoForm: FormArray<FormGroup<CvSectionForm>>;
    otbTemplates: CvTemplateHeaderDTO[];
    userTemplates: CvTemplateHeaderDTO[];
    activeStepIdx = 0;
    newTemplateName: FormControl<string>;

    selectedTemplateId$: BehaviorSubject<string> = new BehaviorSubject(null);

    previewItems: ViewerFile[] = [];
    loadingForPreview: boolean;
    loadingTemplates = true;

    constructor() {
        const fb = this.fb;

        this.steps = fb.array([
            fb.group({
                key: 'esploro.research.cv.page.tab.label.template',
                data: fb.group({ template: null }),
            }),
            fb.group({
                key: 'esploro.research.cv.page.tab.label.info',
                data: this.infoForm,
            }),
            fb.group({
                key: 'esploro.research.cv.page.tab.label.format',
                data: fb.group({ format: null, language: null }),
            }),
        ]);

        this.newTemplateName = fb.control('', Validators.required);
    }

    ngOnInit(): void {
        this.cvCreationService.getCvTemplates().subscribe((templates) => {
            this.otbTemplates = templates.otbTemplates;
            this.userTemplates = templates.userTemplates;
            const selectedTemplateId =
                this.otbTemplates[0]?.id || this.userTemplates[0]?.id;
            if (selectedTemplateId) {
                this.selectedTemplateId$.next(selectedTemplateId);
            }
            this.loadingTemplates = false;
        });

        this.selectedTemplateId$
            .pipe(filter((id) => id !== null))
            .subscribe((id) => {
                this.getTemplate(id);
            });

        this.seoService.updateTitle(this.getPageTitle);
    }

    private get getPageTitle() {
        const instName = this.configurationHandlerService.mainInstitutionName();
        const userName = this.jwtUtilService.getUserDisplayName();
        return `${this._t.instant(
            'esploro.research.cv.page.title',
        )} - ${userName} - ${instName}`;
    }

    goBack() {
        this._window?.history?.back();
    }

    openDialog(): void {
        this.dialog.open(this.successDialogTmplRef(), {
            maxWidth: '33vw',
        });

        this.generateFile()
            .pipe(
                catchError((error: HttpErrorResponse) => {
                    console.error(error);
                    this.dialog.open(this.errorDialogTmplRef());
                    return EMPTY;
                }),
            )
            .subscribe((response: any) => {
                const a = document.createElement('a');
                const objectUrl = URL.createObjectURL(response.body);

                const headers = response.headers;
                const contentDisposition = headers.get('content-disposition');
                const fileName =
                    contentDisposition.split('filename=')[1].split(';')[0] ??
                    '';
                a.href = objectUrl;
                a.download = fileName;
                a.click();
                URL.revokeObjectURL(objectUrl);
            });
    }

    openPreview() {
        this.loadingForPreview = true;
        this.previewItems = [];
        this.dialog.open(this.previewTemplateRef(), {
            panelClass: 'esp-viewer',
            ariaLabel: this._t.instant(
                'research.portal.fulldisplay.file.viewerDialog',
            ),
        });
        const sections = this.getSectionsList();
        this.cvCreationService
            .generateCvFileViewer(
                sections,
                this.steps?.controls?.[2]?.value.data.language,
            )
            .pipe(
                finalize(() => (this.loadingForPreview = false)),
                catchError((error: HttpErrorResponse) => {
                    this.handleErrorGenerateCv();
                    console.error(error);
                    return EMPTY;
                }),
            )
            .subscribe((data: ViewerFile) => {
                if (!data?.url) {
                    this.handleErrorGenerateCv();
                    return;
                }
                this.previewItems = [data];
            });
    }

    openSaveDialog() {
        const dialogRef = this.dialog.open(this.savingDialogTmplRef(), {
            maxWidth: '33vw',
        });
        dialogRef
            .afterClosed()
            .pipe(take(1))
            .subscribe((result) => {
                if (result === 'ok') {
                    const templates = [
                        ...this.otbTemplates,
                        ...this.userTemplates,
                    ];
                    const baseTemplate = templates.find(
                        (t) => t.id === this.selectedTemplateId$.getValue(),
                    );
                    const sections = this.getSectionsList();
                    const lang = this.steps?.controls?.[2]?.value.data.language;
                    const templateNameLang =
                        lang ??
                        baseTemplate.language ??
                        this.multiLanguageService.get3CharLanguage();
                    const header: CvTemplateHeaderDTO = {
                        ...baseTemplate,
                        name: {
                            [templateNameLang]: this.newTemplateName.value,
                        },
                        language: lang,
                        userId: this.jwtUtilService.getUserID(),
                        source: 'RESEARCHER',
                    };

                    delete header.id;
                    delete header.order;

                    this.cvCreationService
                        .saveTemplate(header, sections)
                        .pipe(
                            catchError((error: HttpErrorResponse) => {
                                console.error(error);
                                return EMPTY;
                            }),
                        )
                        .subscribe((data: CvTemplateDTO) => {
                            this.dialog.open(this.savedDialogTmplRef(), {
                                maxWidth: '33vw',
                            });
                            this.userTemplates.push(data.header);
                            this.newTemplateName.reset();
                            this.selectedTemplateId$.next(data.header.id);
                        });
                }
            });
    }

    handleErrorGenerateCv() {
        this.previewItems = [{ url: 'error', contentType: 'example/error' }];
    }

    getSectionsList() {
        const saveListOrder = (section, index: number) => {
            section.order = section.order > 0 ? index + 1 : section.order;
            return section;
        };
        return this.infoForm.value.map(saveListOrder);
    }

    private generateFile() {
        const sections = this.getSectionsList();
        return this.cvCreationService.generateCvFile(
            sections,
            this.steps?.controls?.[2]?.value.data.language,
        );
    }

    getTemplate(templateId: string) {
        const currLang = this.getCurrentTemplate(templateId)?.language;
        this.steps.controls[2].get('data').get('language').patchValue(currLang);
        const parseSections = (sections: any[]) => {
            const form = sections.map((section) => {
                section.primaryFilter = [section.primaryFilter as any];
                section.secondaryFilter = [section.secondaryFilter as any];
                if (section.type === 'researcher') {
                    return this.fb.group<CvResearcherSectionDTO>(
                        new CvResearcherSectionClass(section),
                    );
                } else {
                    return this.fb.group<CvEntitySectionDTO>(
                        new CvEntitySectionClass(section),
                    );
                }
            });

            this.infoForm = this.fb.array(form) as any;
        };
        this.cvCreationService
            .getCvSections(templateId)
            .subscribe(parseSections);
    }

    private getCurrentTemplate(templateId: string) {
        return [
            // ...this.otbTemplates, // TODO: add this when admin OTB configuration will open for editting
            ...this.userTemplates,
        ].find((template) => template.id === templateId);
    }
}
